import React, { useEffect, useState } from 'react';
import { PlusCircle } from 'lucide-react';
import * as api from '../../api';
import { Btn, DatePicker, Input, Select, Textarea, useToast } from '../UI';
import { Panel } from './MTOShared';
import { SYSTEM_SUGGESTIONS } from './mtoUtils';

const today = () => new Date().toISOString().slice(0, 10);

export default function MTONewTakeoff({ onCreated }) {
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    project_id: '', project_division_id: '', discipline_id: '', primary_system: '', other_system: '', boq_id: '',
    drawing_reference: '', drawing_revision: '', date_prepared: today(), remarks: '',
  });
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));

  useEffect(() => {
    api.listProjects(0, 200).then(r => setProjects(r?.items || r || [])).catch(() => {});
    api.listDisciplines(0, 200).then(r => setDisciplines(r?.items || r || [])).catch(() => {});
  }, []);

  // building/zones and BOQs follow the project; BOQs narrow to the chosen building/zone
  useEffect(() => {
    setDivisions([]);
    setF(x => ({ ...x, project_division_id: '', boq_id: '' }));
    if (!f.project_id) return;
    api.listProjectDivisions(f.project_id).then(r => setDivisions(Array.isArray(r) ? r : (r?.items || []))).catch(() => {});
  }, [f.project_id]);

  useEffect(() => {
    setBoqs([]);
    setF(x => ({ ...x, boq_id: '' }));
    if (!f.project_id) return;
    api.getTakeoffBoqOptions(f.project_id, f.project_division_id || undefined).then(r => setBoqs(r || [])).catch(() => {});
  }, [f.project_id, f.project_division_id]);

  const project = projects.find(p => (p.id || p.project_id) === f.project_id);
  const system = (f.primary_system === 'Other' ? f.other_system : f.primary_system).trim();
  const ready = f.project_id && f.project_division_id && f.discipline_id && system;

  const create = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      const t = await api.createTakeoff({
        project_id: f.project_id,
        project_division_id: f.project_division_id,
        discipline_id: f.discipline_id,
        primary_system: system,
        boq_id: f.boq_id || null,
        drawing_reference: f.drawing_reference || null,
        drawing_revision: f.drawing_revision || null,
        date_prepared: f.date_prepared || null,
        remarks: f.remarks || null,
      });
      toast.show(`${t.takeoff_ref} created — break the BOQ lines down into materials.`, 'success');
      onCreated(t.id);
    } catch (e) {
      toast.show(e.message || 'Could not create the take-off.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
        <PlusCircle size={20} /> Start New Material Take-Off
      </h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-secondary)' }}>
        After you click <b>Create Material Take-Off</b>, the BOQ of the selected project, building/zone and system loads
        automatically and becomes the working area — you break each BOQ line down into the actual materials.
      </p>

      <Panel title="Take-off header">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          <Select label="Project *" title="Select Project" value={f.project_id} onChange={set('project_id')}
                  options={projects.map(p => ({ value: p.id || p.project_id, label: `${p.project_code} — ${p.project_name}` }))} />
          <Select label="Building / Zone *" title="Select Building / Zone" value={f.project_division_id} onChange={set('project_division_id')}
                  disabled={!f.project_id} placeholder={f.project_id ? '— Select building/zone —' : 'Select a project first'}
                  options={divisions.map(d => ({ value: d.project_division_id || d.id, label: d.code ? `${d.name} (${d.code})` : d.name }))} />
          <Select label="Discipline *" title="Select Discipline" value={f.discipline_id} onChange={set('discipline_id')}
                  options={disciplines.map(d => ({ value: d.id, label: d.name || d.discipline_name }))} />
          <Select label="Primary System *" title="Select System" value={f.primary_system} onChange={set('primary_system')}
                  options={SYSTEM_SUGGESTIONS.map(s => ({ value: s, label: s }))} />
          {f.primary_system === 'Other' && <Input label="System Name *" value={f.other_system} onChange={set('other_system')} placeholder="e.g. Irrigation" />}
          <Select label="BOQ (loads the working area)" title="Select BOQ" value={f.boq_id} onChange={set('boq_id')}
                  disabled={!f.project_id} placeholder={boqs.length ? '— Select BOQ —' : 'No BOQ for this selection'}
                  options={[{ value: '', label: '— None (add BOQ lines by hand) —' },
                            ...boqs.map(b => ({ value: b.id, label: `${b.name} · ${b.item_count} item${b.item_count === 1 ? '' : 's'}${b.project_division_name ? ` · ${b.project_division_name}` : ''}` }))]} />
          <Input label="Location" value={project?.location || ''} readOnly />
          <Input label="Drawing Reference" value={f.drawing_reference} onChange={set('drawing_reference')} placeholder="e.g. DWG-EXT-01" />
          <Input label="Drawing Revision" value={f.drawing_revision} onChange={set('drawing_revision')} placeholder="e.g. R0" />
          <DatePicker label="Date Prepared" value={f.date_prepared} onChange={set('date_prepared')} />
        </div>
        <div style={{ marginTop: 14 }}>
          <Textarea label="Remarks" value={f.remarks} onChange={set('remarks')} rows={2} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
          <Btn onClick={create} loading={saving} disabled={!ready}>Create Material Take-Off</Btn>
        </div>
      </Panel>
    </div>
  );
}
